import { createZodFormResolver, lt, useFeatureFlag } from '@mews/framework';
import { Banner, Stack } from '@optimus-web/core';
import { CommanderFormProps, useDialogForm, useHandledDialogFormControls } from '@mews-commander/form';
import { ContentSection } from '@mews-commander/ui';
import { PaymentAndCancellationPolicyUpdateParameters, RateGroupOptions } from '@mews-commander/data-access';
import { FeatureFlag } from '@mews-commander/enums';
import {
    EditPaymentPolicyFormValues,
    editPaymentPolicySchema,
    toDefaultPaymentPolicyUpdateParameters,
} from '../../../schemas';
import { LocalPolicyFields } from '../fields';
import { WithDefaultPolicy } from '../../../types';
import { useCurrentRateGroupContext, useCurrentServiceIdContext } from '../../../contexts';
import { LocalCreatePolicyLinks } from './LocalCreatePolicyLinks';

export const useEditDefaultPolicyDialogForm = (params: EditDefaultPolicyFormParams) =>
    useDialogForm({
        id: 'editDefaultPolicyForm',
        renderForm: (props) => <EditDefaultPolicyDialogForm {...props} {...params} />,
        title: lt('RateGroupDefaultPolicyEditFormTitle'),
    });

interface EditDefaultPolicyFormParams extends WithDefaultPolicy {
    policyLinks?: React.ReactNode;
    onSubmit: (formValues: PaymentAndCancellationPolicyUpdateParameters) => Promise<unknown>;
}

const EditDefaultPolicyDialogForm = ({
    defaultPolicy,
    policyLinks = <LocalCreatePolicyLinks />,
    onSubmit,
    ...other
}: EditDefaultPolicyFormParams & CommanderFormProps) => {
    const defaultPolicyFormResolver = createZodFormResolver(editPaymentPolicySchema);
    const { id: rateGroupId, settlementCurrencyCode, enterpriseId, options } = useCurrentRateGroupContext();
    const serviceId = useCurrentServiceIdContext();
    const requirePaymentDuringOCI = useFeatureFlag(FeatureFlag.requirePaymentDuringOnlineCheckIn);

    const { renderFormContent } = useHandledDialogFormControls<EditPaymentPolicyFormValues>({
        ...other,
        values: {
            settlementRules: defaultPolicy.settlementRules.map(({ id }) => id),
            cancellationFees: defaultPolicy.cancellationPolicies.map(({ id }) => id),
            ...(requirePaymentDuringOCI
                ? {
                      requirePaymentCardDuringOnlineCheckIn: options.includes(
                          RateGroupOptions.RequirePaymentCardDuringOnlineCheckIn
                      ),
                  }
                : {}),
        },
        onSubmit: async (values) => {
            const params = editPaymentPolicySchema
                .transform(toDefaultPaymentPolicyUpdateParameters({ rateGroupId, serviceId }))
                .parse({
                    ...values,
                    ...{
                        //  This is momentary while the feature is enable for all users. This will allow us to activate users ad they will have by default the option selected.
                        requirePaymentCardDuringOnlineCheckIn: requirePaymentDuringOCI
                            ? values.requirePaymentCardDuringOnlineCheckIn
                            : true,
                    },
                });

            return onSubmit(params);
        },
        resolver: defaultPolicyFormResolver,
    });

    return renderFormContent(
        <Stack vertical spacing="300" fill>
            <Banner
                variant="info"
                primaryText={lt('RateGroupDefaultPolicyEditFormBannerTitle')}
                secondaryText={lt('RateGroupDefaultPolicyEditFormBannerDescription')}
            />
            <ContentSection
                title={lt('RateGroupDefaultPolicySectionTitle')}
                description={lt('RateGroupDefaultPolicySectionDescription')}
                spacing="300"
            >
                <LocalPolicyFields settlementCurrencyCode={settlementCurrencyCode} enterpriseId={enterpriseId} />
                {policyLinks}
            </ContentSection>
        </Stack>
    );
};
